import { Box, Chip, Autocomplete, TextField } from "@mui/material";
import {
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
  GridFilterOperator,
  GridFilterItem,
  GridFilterInputValueProps,
} from "@mui/x-data-grid-premium";
import { SelectOption } from "./types";
import { useFieldOptions } from "./OptionsContext";

/**
 * Renders multiSelect values as bubble/chip tags
 */
export function MultiSelectCell({ value, field }: GridRenderCellParams) {
  // value is expected to be an array of SelectOption: [{value, label}, ...]
  const options = value as SelectOption[] | null | undefined;

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {options.map((option) => (
        <Chip
          key={option.value}
          label={option.label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.75rem",
            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Renders singleSelect value as a single chip
 */
export function SingleSelectCell({ value }: GridRenderCellParams) {
  const option = value as SelectOption | null | undefined;

  if (!option) {
    return null;
  }

  return (
    <Chip
      label={option.label}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.75rem",
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
}

/**
 * Edit cell component for multiSelect columns
 * Uses MUI Autocomplete with multiple selection
 */
export function MultiSelectEditCell({
  id,
  field,
  value,
}: GridRenderEditCellParams) {
  const apiRef = useGridApiContext();
  const availableOptions = useFieldOptions(field);

  // Current value is array of SelectOption
  const currentValue = (value as SelectOption[] | null) || [];

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: SelectOption[]
  ) => {
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <Autocomplete
      multiple
      options={availableOptions}
      value={currentValue}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={(params) => (
        <TextField {...params} variant="standard" size="small" />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option.label}
              size="small"
              {...rest}
              sx={{ height: 20, fontSize: "0.75rem" }}
            />
          );
        })
      }
      sx={{
        width: "100%",
        "& .MuiAutocomplete-inputRoot": {
          padding: "2px 4px",
        },
      }}
      disableCloseOnSelect
    />
  );
}

/**
 * Edit cell component for singleSelect columns
 */
export function SingleSelectEditCell({
  id,
  field,
  value,
}: GridRenderEditCellParams) {
  const apiRef = useGridApiContext();
  const availableOptions = useFieldOptions(field);

  const currentValue = value as SelectOption | null;

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: SelectOption | null
  ) => {
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <Autocomplete
      options={availableOptions}
      value={currentValue}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={(params) => (
        <TextField {...params} variant="standard" size="small" />
      )}
      sx={{
        width: "100%",
        "& .MuiAutocomplete-inputRoot": {
          padding: "2px 4px",
        },
      }}
    />
  );
}

/**
 * Filter input component for multiSelect columns
 * Allows user to select one or more options to filter by
 */
function MultiSelectFilterInput({
  item,
  applyValue,
  field,
}: GridFilterInputValueProps & { field: string }) {
  const availableOptions = useFieldOptions(field);

  // Value is stored as array of option values (not full SelectOption objects)
  const currentValues = (item.value as any[]) || [];

  // Convert stored values back to SelectOption objects for display
  const selectedOptions = availableOptions.filter((opt) =>
    currentValues.includes(opt.value)
  );

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: SelectOption[]
  ) => {
    // Store just the values, not full objects
    applyValue({ ...item, value: newValue.map((opt) => opt.value) });
  };

  return (
    <Autocomplete
      multiple
      options={availableOptions}
      value={selectedOptions}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Value"
          placeholder="Select options..."
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option.label}
              size="small"
              {...rest}
              sx={{ height: 20, fontSize: "0.75rem" }}
            />
          );
        })
      }
      sx={{ minWidth: 200 }}
      disableCloseOnSelect
    />
  );
}

/**
 * Creates filter operators for multiSelect columns
 * @param field - The field name to get options for
 */
export function getMultiSelectFilterOperators(
  field: string
): GridFilterOperator[] {
  return [
    {
      label: "contains any of",
      value: "containsAny",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.value ||
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0
        ) {
          return null;
        }
        const filterValues = filterItem.value;
        return (cellValue: SelectOption[] | null | undefined) => {
          if (!cellValue || !Array.isArray(cellValue)) return false;
          // Check if any of the cell values match any of the filter values
          return cellValue.some((opt) => filterValues.includes(opt.value));
        };
      },
      InputComponent: (props: GridFilterInputValueProps) => (
        <MultiSelectFilterInput {...props} field={field} />
      ),
    },
    {
      label: "contains all of",
      value: "containsAll",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.value ||
          !Array.isArray(filterItem.value) ||
          filterItem.value.length === 0
        ) {
          return null;
        }
        const filterValues = filterItem.value;
        return (cellValue: SelectOption[] | null | undefined) => {
          if (!cellValue || !Array.isArray(cellValue)) return false;
          const cellValues = cellValue.map((opt) => opt.value);
          // Check if all filter values are present in cell values
          return filterValues.every((fv: any) => cellValues.includes(fv));
        };
      },
      InputComponent: (props: GridFilterInputValueProps) => (
        <MultiSelectFilterInput {...props} field={field} />
      ),
    },
    {
      label: "is empty",
      value: "isEmpty",
      getApplyFilterFn: () => {
        return (cellValue: SelectOption[] | null | undefined): boolean => {
          return !cellValue || cellValue.length === 0;
        };
      },
      requiresFilterValue: false,
    },
    {
      label: "is not empty",
      value: "isNotEmpty",
      getApplyFilterFn: () => {
        return (cellValue: SelectOption[] | null | undefined): boolean => {
          return Boolean(cellValue && cellValue.length > 0);
        };
      },
      requiresFilterValue: false,
    },
  ];
}
